import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Added this import
import Layout from '../../components/Layout';
import StatsCard from '../../components/StatsCard';
import Card from '../../components/Card';
import StatusChart from '../../components/StatusChart'; // Added this import
import TechnicianChart from '../../components/TechnicianChart'; // Added this import
import { 
  Users, 
  Wrench, 
  FileText, 
  Clock, 
  Euro,
} from 'lucide-react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  adminCount: number;
  techCount: number;
  clientCount: number;
  newUsersThisMonth: number;
}

interface MissionStats {
  totalMissions: number;
  inProgressMissions: number;
  recentMissions: {
    missionNumber: string;
    clientName: string;
    technicianName: string;
    serviceTypeName: string;
    scheduledDate: string;
  }[];
}

interface QuoteStats {
  totalQuotes: number;
  pendingQuotes: number;
  quotesThisMonth: number;
  quotesLastMonth: number;
}

interface PaymentStats {
  totalRevenue: number;
  monthlyRevenue: number;
  lastMonthlyRevenue: number;
}

interface RequestStats {
  totalRequests: number;
  pendingRequests: number;
  assignedRequests: number;
  inProgressRequests: number;
  completedRequests: number;
  urgentRequests: number;
  newRequestsThisMonth: number;
  avgProcessingTime: number;
  typeDistribution: { _id: string; count: number }[];
}

interface StatsOverview {
  totalRevenue: number;
  totalMissions: number;
  totalQuotes: number;
  totalCompanies: number;
  totalUsers: number;
  periodStartDate: string;
  periodEndDate: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate(); // Initialized useNavigate

  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [missionStats, setMissionStats] = useState<MissionStats | null>(null);
  const [quoteStats, setQuoteStats] = useState<QuoteStats | null>(null);
  const [paymentStats, setPaymentStats] = useState<PaymentStats | null>(null);
  const [requestStats, setRequestStats] = useState<RequestStats | null>(null);
  const [statsOverview, setStatsOverview] = useState<StatsOverview | null>(null);
  const [missionsByStatus, setMissionsByStatus] = useState<any[]>([]); // New state for missions by status
  const [missionsByTechnician, setMissionsByTechnician] = useState<any[]>([]);
  const [todayMissions, setTodayMissions] = useState<any[]>([]); // New state for today's missions
  const [recentMissionsWithReports, setRecentMissionsWithReports] = useState<any[]>([]); // New state for recent missions with reports
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear previous errors

        let startDate: Date;
        let endDate: Date;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day

        switch (selectedPeriod) {
          case 'day':
            startDate = today;
            endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
            break;
          case 'week':
            startDate = new Date(today.setDate(today.getDate() - today.getDay())); // Start of current week (Sunday)
            endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6, 23, 59, 59, 999);
            break;
          case 'month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
            break;
          case 'year':
            startDate = new Date(today.getFullYear(), 0, 1);
            endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
            break;
          default:
            startDate = new Date(today.getFullYear(), today.getMonth(), 1); // Default to current month
            endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
            break;
        }

        const dateParams = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };

        // Fetch overall stats with date range
        const statsOverviewRes = await apiService.dashboard.getAdminStatsOverview(dateParams);
        setStatsOverview(statsOverviewRes.data.data);

        // Fetch data for charts with date range
        const missionsByStatusRes = await apiService.dashboard.getAdminMissionsByStatus(dateParams);
        const missionsByTechnicianRes = await apiService.dashboard.getAdminMissionsByTechnician(dateParams);

        setMissionsByStatus(missionsByStatusRes.data.data);
        setMissionsByTechnician(missionsByTechnicianRes.data.data);
        const todayMissionsRes = await apiService.dashboard.getAdminMissionsByDate(new Date().toISOString().split('T')[0]); // Fetch today's missions
        const recentMissionsWithReportsRes = await apiService.dashboard.getAdminRecentMissionsWithReports(); // Fetch recent missions with reports

        setTodayMissions(todayMissionsRes.data.data); // Set new state
        setRecentMissionsWithReports(recentMissionsWithReportsRes.data.data); // Set new state

        // Fetch other detailed stats (keeping existing for now)
        const [usersRes, missionsRes, quotesRes, paymentsRes, requestsRes] = await Promise.all([
          apiService.users.getStats(),
          apiService.missions.getStatsOverview(),
          apiService.quotes.getStatsOverview(),
          apiService.payments.getStats(),
          apiService.requests.getStats(),
        ]);

        setUserStats(usersRes.data.data);
        setMissionStats(missionsRes.data.data);
        setQuoteStats(quotesRes.data.data);
        setPaymentStats(paymentsRes.data.data);
        setRequestStats(requestsRes.data.data);

        console.log('Missions by Status (filtered):', missionsByStatusRes);
        console.log('Missions by Technician (filtered):', missionsByTechnicianRes);
        console.log('Today\'s Missions:', todayMissionsRes);
        console.log('Recent Missions with Reports:', recentMissionsWithReportsRes);

      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Erreur lors du chargement des données du tableau de bord.");
        toast.error("Erreur lors du chargement des données du tableau de bord.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <p>Chargement du tableau de bord...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen text-red-500">
          <p>{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard Administrateur
          </h1>
          <p className="text-gray-600">Vue d'overview de l'activité elecborn</p>

          {/* Period Selection */}
          <div className="mt-4 flex space-x-2">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedPeriod === 'day'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedPeriod('day')}
            >
              Jour
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedPeriod === 'week'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedPeriod('week')}
            >
              Semaine
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedPeriod === 'month'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedPeriod('month')}
            >
              Mois
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedPeriod === 'year'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedPeriod('year')}
            >
              Année
            </button>
          </div>
        </div>

        {/* Creation Shortcuts */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Raccourcis de Création</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg md:px-8"
              onClick={() => navigate('/admin/requests/add')}
            >
              <FileText className="mr-2" /> Créer Demande
            </button>
            <button
              className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 md:py-4 md:text-lg md:px-8"
              onClick={() => navigate('/admin/quotes/add')}
            >
              <Euro className="mr-2" /> Créer Devis
            </button>
            <button
              className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 md:py-4 md:text-lg md:px-8"
              onClick={() => navigate('/admin/add-mission')}
            >
              <Wrench className="mr-2" /> Créer Mission
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"> {/* Changed to lg:grid-cols-5 for 5 cards */}
          <StatsCard
            title="Utilisateurs"
            value={statsOverview?.totalUsers.toString() || 'N/A'}
            icon={Users}
            change=""
            changeType="neutral"
            color="primary"
            onClick={() => navigate(`/admin/user-management?startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
          <StatsCard
            title="Entreprises"
            value={statsOverview?.totalCompanies.toString() || 'N/A'}
            icon={Users} // Reusing Users icon for now, consider adding a specific Company icon if available
            change=""
            changeType="neutral"
            color="info"
            onClick={() => navigate(`/admin/company-management?startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
          <StatsCard
            title="Missions"
            value={statsOverview?.totalMissions.toString() || 'N/A'}
            icon={Wrench}
            change=""
            changeType="neutral"
            color="secondary"
            onClick={() => navigate(`/admin/mission-management?startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
          <StatsCard
            title="Devis"
            value={statsOverview?.totalQuotes.toString() || 'N/A'}
            icon={FileText}
            change=""
            changeType="neutral"
            color="warning"
            onClick={() => navigate(`/admin/quote-management?startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
          <StatsCard
            title="Chiffre d'affaires"
            value={`${statsOverview?.totalRevenue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) || 'N/A'}`}
            icon={Euro}
            change=""
            changeType="positive"
            color="success"
            onClick={() => navigate(`/admin/payment-management?startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
        </div>

        {/* Charts and Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <StatusChart
            title="Missions par Statut"
            data={missionsByStatus}
            onStatusClick={(status) => navigate(`/admin/mission-management?status=${status}&startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)}
          />
          <TechnicianChart
            title="Missions par Technicien"
            data={missionsByTechnician}
            onTechnicianClick={(data) => navigate(`/admin/mission-management?technicianId=${data.technicianId}&startDate=${statsOverview?.periodStartDate}&endDate=${statsOverview?.periodEndDate}`)} // Use data._id for technicianId
          />
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Interventions Récentes</h3>
            <div className="space-y-4">
              {recentMissionsWithReports.length === 0 ? (
                <p className="text-gray-500">Aucune intervention récente.</p>
              ) : (
                recentMissionsWithReports.map((mission, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/admin/view-report/${mission.report._id}`)}> {/* Navigate to report */}
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${mission.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                      <div>
                        <p className="font-medium text-gray-900">{mission.clientName}</p>
                        <p className="text-sm text-gray-500">{mission.serviceType?.name}</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">{new Date(mission.scheduledDate).toLocaleDateString()}</span>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Planning Aujourd'hui</h3>
            <div className="space-y-4">
              {todayMissions.length === 0 ? (
                <p className="text-gray-500">Aucun planning pour aujourd'hui.</p>
              ) : (
                todayMissions.map((mission, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-primary-50 rounded-lg cursor-pointer"
                    onClick={() => navigate(`/admin/view-mission/${mission._id}`)}> {/* Navigate to mission details */}
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{mission.clientName}</p>
                        <p className="text-sm text-gray-500">{mission.technicianName}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-primary-600">{new Date(mission.scheduledDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;